import React, { useState, useEffect, useRef } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Grid,
  MenuItem,
  Select,
  Pagination,
  TextField,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Skeleton,
  Tooltip,
  Badge,
  Stack,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
  ShoppingCart as OrderIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  LocalShipping as ShippingIcon,
  Receipt as ReceiptIcon,
  Comment as CommentIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { Order, ApiResponse } from '../types/api';

const Orders: React.FC = () => {
  // Pagination and filters state
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [orders, setOrders] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [productName, setProductName] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Use the real API, not demo
  const { data: ordersResponse, isLoading, error }: UseQueryResult<any> = useQuery({
    queryKey: ['orders-real'],
    queryFn: () => api.getOrdersFromApi(),
  });
  const ordersData = ordersResponse?.orders;

  // State for selected order and modal
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [orderComments, setOrderComments] = useState<any[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');
  const [addCommentLoading, setAddCommentLoading] = useState(false);
  const [addCommentError, setAddCommentError] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const commentInputRef = useRef<HTMLInputElement>(null);

  // Utility function to get vendor phone (without falling back to customer phone)
  const getVendorPhone = (orderDetails: any) => {
    // Only get phone from vendor sources, NOT customer data
    const vendorPhone = orderDetails.store?.phone
      || orderDetails.stores?.[0]?.phone
      || orderDetails.store?.address?.phone
      || orderDetails.stores?.[0]?.address?.phone;
    
    return vendorPhone || null; // Return null if no vendor phone found
  };

  // Utility function to get vendor info by fetching from Dokan API
  const [vendorInfo, setVendorInfo] = useState<any>(null);
  const [vendorLoading, setVendorLoading] = useState(false);

  const fetchVendorInfo = async (orderDetails: any) => {
    // Try to get vendor ID from the order
    let vendorId: string | number | null = null;
    
    // Check line items for vendor/author information
    if (orderDetails.line_items && orderDetails.line_items.length > 0) {
      // Try to get vendor ID from product meta or line item meta
      for (const item of orderDetails.line_items) {
        if (item.meta_data) {
          const vendorMeta = item.meta_data.find((m: any) => 
            m.key === 'vendor_id' || m.key === 'author' || m.key === 'dokan_vendor_id'
          );
          if (vendorMeta && vendorMeta.value) {
            vendorId = vendorMeta.value;
            break;
          }
        }
      }
    }

    // If no vendor ID found, try to get from order meta
    if (!vendorId && orderDetails.meta_data) {
      const vendorMeta = orderDetails.meta_data.find((m: any) => 
        m.key === 'vendor_id' || m.key === 'dokan_vendor_id' || m.key === '_dokan_vendor_id'
      );
      if (vendorMeta && vendorMeta.value) {
        vendorId = vendorMeta.value;
      }
    }

    // If we have a vendor ID, fetch vendor details
    if (vendorId) {
      setVendorLoading(true);
      try {
        const response = await fetch(`https://api.erp.oroud.net/api/vendors.php`);
        const vendorsData = await response.json();
        const vendor = vendorsData.vendors?.find((v: any) => v.id == vendorId);
        if (vendor) {
          setVendorInfo(vendor);
        }
      } catch (error) {
        console.error('Failed to fetch vendor info:', error);
      } finally {
        setVendorLoading(false);
      }
    }
  };

  // Get marketing traffic source from order meta data
  const getTrafficSource = (orderDetails: any) => {
    const meta = orderDetails.meta_data || [];
    
    // Look for common marketing attribution fields
    const sourceFields = [
      'utm_source',
      'traffic_source', 
      'referrer_source',
      'marketing_source',
      'campaign_source',
      '_utm_source',
      '_traffic_source'
    ];

    for (const field of sourceFields) {
      const metaItem = meta.find((m: any) => m.key === field);
      if (metaItem && metaItem.value && metaItem.value !== 'android-app') {
        return metaItem.value;
      }
    }

    // Look for UTM campaign data
    const campaignMeta = meta.find((m: any) => m.key === 'utm_campaign' || m.key === '_utm_campaign');
    if (campaignMeta && campaignMeta.value) {
      return `Campaign: ${campaignMeta.value}`;
    }

    // Look for referrer data
    const referrerMeta = meta.find((m: any) => m.key === 'referrer' || m.key === '_wp_http_referer');
    if (referrerMeta && referrerMeta.value) {
      try {
        const url = new URL(referrerMeta.value);
        const hostname = url.hostname.toLowerCase();
        if (hostname.includes('google')) return 'Google (Organic)';
        if (hostname.includes('facebook')) return 'Facebook';
        if (hostname.includes('instagram')) return 'Instagram';
        if (hostname.includes('youtube')) return 'YouTube';
        if (hostname.includes('tiktok')) return 'TikTok';
        return `Referrer: ${hostname}`;
      } catch {
        // If URL parsing fails, just show the referrer
        return `Referrer: ${referrerMeta.value}`;
      }
    }

    // Default fallback
    return 'Direct/Unknown';
  };

  // Utility function to get status color
  const getStatusColor = (status: string): 'success' | 'warning' | 'info' | 'error' | 'default' => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'pending': 
      case 'on-hold': return 'warning';
      case 'processing': return 'info';
      case 'cancelled': 
      case 'refunded': 
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  // Fetch order details when an order is clicked
  const handleOrderClick = async (orderId: number) => {
    setSelectedOrderId(orderId);
    setOrderDetails(null);
    setDetailsError(null);
    setOrderComments([]);
    setCommentsError(null);
    setVendorInfo(null); // Reset vendor info
    setDetailsLoading(true);
    setCommentsLoading(true);
    try {
      const details = await api.getOrder(orderId);
      setOrderDetails(details);
      // Fetch vendor info after getting order details
      await fetchVendorInfo(details);
    } catch (err: any) {
      setDetailsError(err?.message || 'Failed to load order details');
    } finally {
      setDetailsLoading(false);
    }
    try {
      const commentsRes = await api.getComments(orderId);
      setOrderComments(commentsRes.data || []);
    } catch (err: any) {
      setCommentsError('Failed to load order notes');
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedOrderId(null);
    setOrderDetails(null);
    setDetailsError(null);
  };

  // Add comment handler
  async function handleAddComment() {
    if (!selectedOrderId || !newComment.trim()) return;
    setAddCommentLoading(true);
    setAddCommentError(null);
    try {
      await api.addComment(selectedOrderId, newComment, false);
      setNewComment('');
      // Refocus input
      commentInputRef.current?.focus();
      // Refresh comments
      try {
        const commentsRes = await api.getComments(selectedOrderId);
        setOrderComments(commentsRes.data || []);
      } catch {}
    } catch (err: any) {
      setAddCommentError('Failed to add note');
    } finally {
      setAddCommentLoading(false);
    }
  }

  // Search handler (applies filters and resets page)
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  // Clear filters
  const handleClearFilters = () => {
    setCustomerName('');
    setProductName('');
    setDateFrom('');
    setDateTo('');
    setStatusFilter('');
    setPage(1);
  };

  // Fetch orders (filtered or not) with server-side pagination
  useEffect(() => {
    let ignore = false;
    setOrdersLoading(true);
    setOrdersError(null);
    
    // If date filter is active, fetch all (no pagination)
    const dateFilterActive = !!dateFrom || !!dateTo;
    const params: any = { 
      page: dateFilterActive ? 1 : page, // Use actual page when no date filter
      per_page: dateFilterActive ? 10000 : perPage 
    };
    
    if (customerName) params.customer_name = customerName;
    if (productName) params.product_name = productName;
    if (statusFilter) params.status = statusFilter;
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (dateFrom && dateRegex.test(dateFrom)) params.date_from = dateFrom;
    if (dateTo && dateRegex.test(dateTo)) params.date_to = dateTo;
    
    console.log('Fetching orders with params:', params); // Debug log to verify pagination
    
    api.getOrdersPaginated(params)
      .then((res: any) => {
        if (ignore) return;
        console.log('Orders response:', res); // Debug log
        setOrders(res.orders || []);
        setTotalPages(res.total_pages || 1);
        setTotalOrders(res.total_orders || 0);
      })
      .catch((err: any) => {
        if (ignore) return;
        setOrdersError(err?.message || 'Failed to fetch orders');
      })
      .finally(() => {
        if (ignore) return;
        setOrdersLoading(false);
      });
    return () => { ignore = true; };
  }, [page, perPage, customerName, productName, dateFrom, dateTo, statusFilter]);

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Orders</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress size={60} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Orders</Typography>
        <Alert severity="error">
          Error loading orders: {(error as Error).message}
        </Alert>
      </Box>
    );
  }

  const hasActiveFilters = customerName || productName || dateFrom || dateTo || statusFilter;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      <OrderIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Orders Management
        </Typography>
        <Badge badgeContent={totalOrders} color="primary" max={9999}>
          <OrderIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        </Badge>
      </Box>

      {/* Enhanced Search Form */}
      <Card elevation={3} sx={{ mb: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
              🔍 Search & Filter Orders
            </Typography>
            {hasActiveFilters && (
              <Chip 
                label="Filters Active" 
                color="primary" 
                size="small" 
                sx={{ ml: 2 }}
                icon={<SearchIcon />}
              />
            )}
          </Box>
          
          <form onSubmit={handleSearch}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Customer Name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ color: 'action.active', mr: 1 }} />,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white' } }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Product Name"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  InputProps={{
                    startAdornment: <OrderIcon sx={{ color: 'action.active', mr: 1 }} />,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white' } }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="From Date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white' } }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="To Date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white' } }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Status Filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white' } }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="processing">Processing</MenuItem>
                  <MenuItem value="on-hold">On Hold</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="refunded">Refunded</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </TextField>
              </Grid>
              
              <Grid item xs={12} md={2}>
                <Stack direction="row" spacing={1}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={ordersLoading}
                    startIcon={ordersLoading ? <CircularProgress size={16} /> : <SearchIcon />}
                    sx={{ 
                      background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                      boxShadow: '0 3px 5px 2px rgba(25, 118, 210, .3)',
                    }}
                  >
                    Search
                  </Button>
                  <IconButton
                    onClick={handleClearFilters}
                    disabled={ordersLoading || !hasActiveFilters}
                    sx={{ bgcolor: 'white', '&:hover': { bgcolor: 'grey.100' } }}
                  >
                    <ClearIcon />
                  </IconButton>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Orders Table */}
      {ordersLoading ? (
        <Card>
          <CardContent>
            <Box sx={{ p: 2 }}>
              {[...Array(5)].map((_, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Skeleton variant="text" width="10%" height={40} sx={{ mr: 2 }} />
                  <Skeleton variant="text" width="20%" height={40} sx={{ mr: 2 }} />
                  <Skeleton variant="text" width="30%" height={40} sx={{ mr: 2 }} />
                  <Skeleton variant="text" width="20%" height={40} sx={{ mr: 2 }} />
                  <Skeleton variant="rectangular" width="15%" height={32} />
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      ) : ordersError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {ordersError}
        </Alert>
      ) : (
        <Card elevation={2}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: 'primary.main' }}>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Order ID</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date & Time</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Customer</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <OrderIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary">
                            No orders found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {hasActiveFilters ? 'Try adjusting your search filters' : 'Orders will appear here when available'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : orders.map((order: any, index) => (
                    <TableRow 
                      key={order.id} 
                      hover 
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'action.hover' },
                        backgroundColor: index % 2 === 0 ? 'grey.50' : 'white'
                      }}
                    >
                      <TableCell onClick={() => handleOrderClick(order.id)}>
                        <Typography variant="body2" fontWeight={600} color="primary.main">
                          #{order.id}
                        </Typography>
                      </TableCell>
                      <TableCell onClick={() => handleOrderClick(order.id)}>
                        <Typography variant="body2">
                          {new Date(order.date_created || order.created_at).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell onClick={() => handleOrderClick(order.id)}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'primary.light' }}>
                            <PersonIcon />
                          </Avatar>
                          <Typography variant="body2">
                            {order.billing?.first_name || order.customer_name || 'Unknown'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right" onClick={() => handleOrderClick(order.id)}>
                        <Typography variant="body2" fontWeight={600}>
                          {order.total || order.total_amount} {order.currency_symbol || 'MAD'}
                        </Typography>
                      </TableCell>
                      <TableCell onClick={() => handleOrderClick(order.id)}>
                        <Chip
                          label={order.status}
                          size="small"
                          color={getStatusColor(order.status)}
                          sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOrderClick(order.id)}
                            sx={{ color: 'primary.main' }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {!dateFrom && !dateTo && totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
            size="large"
          />
        </Box>
      )}

      {/* Summary Info */}
      {!dateFrom && !dateTo && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing page {page} of {totalPages} • Total: {totalOrders.toLocaleString()} orders
          </Typography>
        </Box>
      )}

      {/* Enhanced Order Details Modal */}
      <Dialog 
        open={!!selectedOrderId} 
        onClose={handleCloseModal} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, maxHeight: '90vh' }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <ReceiptIcon />
          Order Details {orderDetails && `#${orderDetails.id}`}
        </DialogTitle>
        
        <DialogContent dividers sx={{ p: 0 }}>
          {detailsLoading && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={60} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading order details...
              </Typography>
            </Box>
          )}
          
          {detailsError && (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{detailsError}</Alert>
            </Box>
          )}
          
          {orderDetails && (
            <Box sx={{ p: 3 }}>
              {/* Order Header */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Order #{orderDetails.id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(orderDetails.date_created || orderDetails.created_at).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                      <Typography variant="body2" sx={{ mr: 1 }}>Status:</Typography>
                                              <Select
                        size="small"
                        value={orderDetails.status}
                        onChange={async (e) => {
                          const newStatus = e.target.value;
                          setStatusLoading(true);
                          setStatusError(null);
                          try {
                            await api.updateOrderStatus(orderDetails.id, newStatus);
                            setOrderDetails({ ...orderDetails, status: newStatus });
                          } catch (err: any) {
                            setStatusError('Failed to update status');
                          } finally {
                            setStatusLoading(false);
                          }
                        }}
                        disabled={statusLoading}
                        sx={{ minWidth: 140 }}
                      >
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="processing">Processing</MenuItem>
                        <MenuItem value="on-hold">On Hold</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                        <MenuItem value="refunded">Refunded</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                      </Select>
                      {statusLoading && <CircularProgress size={16} />}
                    </Box>
                    {statusError && (
                      <Typography color="error" variant="caption" display="block">
                        {statusError}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>

              <Grid container spacing={3}>
                {/* Customer Information */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <PersonIcon color="primary" />
                        Customer Information
                      </Typography>
                      
                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            <strong>Name:</strong> {orderDetails.billing?.first_name || orderDetails.customer_name || 'N/A'}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            <strong>Email:</strong> {orderDetails.billing?.email || 'N/A'}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            <strong>Phone:</strong> {orderDetails.billing?.phone || orderDetails.shipping?.phone || 'N/A'}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <LocationIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.2 }} />
                          <Typography variant="body2">
                            <strong>Address:</strong> {[
                              orderDetails.billing?.address_1,
                              orderDetails.billing?.address_2,
                              orderDetails.billing?.city,
                              orderDetails.billing?.state,
                              orderDetails.billing?.postcode,
                              orderDetails.billing?.country
                            ].filter(Boolean).join(', ') || 'N/A'}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Vendor Information - FIXED */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <StoreIcon color="primary" />
                        Vendor Information
                      </Typography>
                      
                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            <strong>Store:</strong> {
                              vendorInfo?.store_name || vendorInfo?.name || 
                              orderDetails.store?.shop_name || orderDetails.stores?.[0]?.shop_name || 'N/A'
                            }
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            <strong>Vendor Phone:</strong> {
                              vendorLoading ? (
                                <CircularProgress size={12} sx={{ ml: 1 }} />
                              ) : vendorInfo?.phone ? (
                                vendorInfo.phone
                              ) : getVendorPhone(orderDetails) ? (
                                getVendorPhone(orderDetails)
                              ) : (
                                'Not Available'
                              )
                            }
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <LocationIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.2 }} />
                          <Typography variant="body2">
                            <strong>Address:</strong> {
                              vendorInfo?.address ? (
                                typeof vendorInfo.address === 'object' ? 
                                  [vendorInfo.address.street_1, vendorInfo.address.city, vendorInfo.address.country].filter(Boolean).join(', ') :
                                  vendorInfo.address
                              ) : 'N/A'
                            }
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            <strong>Traffic Source:</strong> {getTrafficSource(orderDetails)}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Order Items */}
              <Card variant="outlined" sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <OrderIcon color="primary" />
                    Order Items
                  </Typography>
                  
                  {orderDetails.line_items?.map((item: any) => (
                    <Box 
                      key={item.id} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        mb: 2, 
                        p: 2, 
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'grey.50'
                      }}
                    >
                      {item.image?.src ? (
                        <Avatar
                          src={item.image.src}
                          alt={item.name}
                          sx={{ width: 64, height: 64, mr: 2, borderRadius: 1 }}
                          variant="rounded"
                        />
                      ) : (
                        <Avatar
                          sx={{ width: 64, height: 64, mr: 2, borderRadius: 1, bgcolor: 'grey.200' }}
                          variant="rounded"
                        >
                          <OrderIcon />
                        </Avatar>
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>{item.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.price} {orderDetails.currency_symbol || 'MAD'} × {item.quantity}
                        </Typography>
                      </Box>
                      <Typography variant="h6" fontWeight={600}>
                        {item.total} {orderDetails.currency_symbol || 'MAD'}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>

              {/* Shipping & Summary */}
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <ShippingIcon color="primary" />
                        Shipping Information
                      </Typography>
                      
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Method:</strong> {orderDetails.shipping_lines?.[0]?.method_title || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Articles:</strong> {orderDetails.shipping_lines?.[0]?.meta_data?.find((m: any) => m.key === 'Articles')?.value || 'N/A'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <ReceiptIcon color="primary" />
                        Order Summary
                      </Typography>
                      
                      <Grid container spacing={1}>
                        <Grid item xs={8}>
                          <Typography variant="body2">Items Subtotal:</Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: 'right' }}>
                          <Typography variant="body2">
                            {orderDetails.line_items?.reduce((sum: number, item: any) => sum + Number(item.total), 0).toFixed(2)} {orderDetails.currency_symbol || 'MAD'}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={8}>
                          <Typography variant="body2">Shipping:</Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: 'right' }}>
                          <Typography variant="body2">
                            {orderDetails.shipping_total} {orderDetails.currency_symbol || 'MAD'}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                        </Grid>
                        
                        <Grid item xs={8}>
                          <Typography variant="subtitle1" fontWeight={600}>Order Total:</Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: 'right' }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {orderDetails.total} {orderDetails.currency_symbol || 'MAD'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Order Notes */}
              <Card variant="outlined" sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CommentIcon color="primary" />
                    Order Notes
                  </Typography>
                  
                  {commentsLoading ? (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Loading notes...
                      </Typography>
                    </Box>
                  ) : commentsError ? (
                    <Alert severity="error">{commentsError}</Alert>
                  ) : orderComments.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No notes for this order.
                    </Typography>
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      {orderComments.map((note) => (
                        <Box 
                          key={note.id} 
                          sx={{ 
                            mb: 1, 
                            p: 2, 
                            bgcolor: 'grey.50', 
                            borderRadius: 1,
                            borderLeft: '4px solid',
                            borderLeftColor: 'primary.main'
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {note.author} • {new Date(note.created_at).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {note.comment}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                  
                  {/* Add new comment */}
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Add a note..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      disabled={addCommentLoading}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddComment();
                      }}
                      InputProps={{
                        startAdornment: <EditIcon sx={{ color: 'action.active', mr: 1 }} />,
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddComment}
                      disabled={addCommentLoading || !newComment.trim()}
                      sx={{ minWidth: 100 }}
                    >
                      {addCommentLoading ? 'Adding...' : 'Add Note'}
                    </Button>
                  </Box>
                  
                  {addCommentError && (
                    <Alert severity="error" sx={{ mt: 1 }}>{addCommentError}</Alert>
                  )}
                  
                  {/* Customer Note */}
                  {orderDetails.customer_note && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                      <Typography variant="subtitle2" color="info.dark">Customer Note:</Typography>
                      <Typography variant="body2" color="info.dark">{orderDetails.customer_note}</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseModal} variant="outlined" size="large">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Orders;
